/**
 * ScheduleGrid — wizualny grafik zmian (Grafiki i RCP, Etap 2).
 *
 * Tygodniowa siatka: wiersze = pracownicy (+ „Nieprzypisane" dla zmian bez pracownika),
 * kolumny = dni (Pon–Ndz), komórki = bloki zmian (kolor wg typu, zakres godzin + miejsce).
 * Read-only; „Nowa zmiana" prowadzi do formularza modułu Kreatora `grafiki_zmiany`.
 *
 * Dane: rekordy modułu `grafiki_zmiany` (płaskie wiersze, wartości po slugu pola —
 * `pracownik`/`data_od`/`data_do`/`typ`/`miejsce`/`status`) przez `/api/modules/{slug}/records`;
 * nazwy pracowników z `useEmployeesList`. Brak biblioteki dat — helpery tygodnia ręcznie
 * (wzorzec z components/modules/CalendarView.tsx). Zakres firmy z przełącznika firm.
 *
 * Dostęp: trasa `/grafik` owinięta `ModuleGuard moduleCode="grafiki"` (TenantTabPanels). Błąd
 * odczytu rekordów NIE jest połykany do pustej siatki — 404 (moduł bez rodzaju danych
 * `grafiki_zmiany`) → stan „grafik niedostępny" bez „Nowa zmiana"; inny błąd → stan z ponowieniem.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { CalendarOff, CalendarPlus, ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatDictionaryOptionLabel, useDictionary } from '@/hooks/useDictionary';
import { api } from '@/lib/api';
import { useActiveCompanyScope } from '@/lib/use-active-company';
import { useEmployeesList } from '@/lib/use-tenant-people';

const SHIFT_SLUG = 'grafiki_zmiany';

interface ShiftRecord {
  id: string;
  record_name: string | null;
  pracownik?: string | null;
  data_od?: string | null;
  data_do?: string | null;
  typ?: string | null;
  miejsce?: string | null;
  status?: string | null;
  [k: string]: unknown;
}

// Wartości = klucze i18n; tłumaczenie w miejscu użycia (t() jest w scope komponentu).
const TYP_LABEL: Record<string, string> = {
  poranna: 'hr.schedule.types.poranna',
  popoludniowa: 'hr.schedule.types.popoludniowa',
  nocna: 'hr.schedule.types.nocna',
  dzienna: 'hr.schedule.types.dzienna',
  weekend: 'hr.schedule.types.weekend',
};

// Kolor bloku zmiany wg typu (Tailwind, light + dark).
const TYP_CLASSES: Record<string, string> = {
  poranna: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-200',
  popoludniowa: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/40 dark:text-blue-200',
  nocna:
    'bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-900/40 dark:text-indigo-200',
  dzienna: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-200',
  weekend:
    'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/40 dark:text-purple-200',
};
const TYP_DEFAULT = 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-700 dark:text-gray-200';

// Wartości = klucze i18n; tłumaczenie w miejscu użycia (t() jest w scope komponentu).
const WEEK_DAYS_PL = [
  'hr.schedule.days.monday',
  'hr.schedule.days.tuesday',
  'hr.schedule.days.wednesday',
  'hr.schedule.days.thursday',
  'hr.schedule.days.friday',
  'hr.schedule.days.saturday',
  'hr.schedule.days.sunday',
];

/** Poniedziałek tygodnia zawierającego `d` (kopia, bez mutacji, lokalny czas). */
function startOfWeekMonday(d: Date): Date {
  const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = (copy.getDay() + 6) % 7; // Pon=0 … Ndz=6
  copy.setDate(copy.getDate() - dow);
  return copy;
}

function addDays(d: Date, n: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
}

/** `YYYY-MM-DD` w czasie lokalnym (bez przesunięcia UTC). */
function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
}

function fmtDayHeader(d: Date): string {
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Czas trwania zmiany w godzinach (z data_od/data_do). 0 gdy brak/parsowanie zawiedzie. */
function shiftHours(s: ShiftRecord): number {
  if (!s.data_od || !s.data_do) return 0;
  const a = new Date(s.data_od).getTime();
  const b = new Date(s.data_do).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b <= a) return 0;
  return (b - a) / 3_600_000;
}

/** Format godzin: „7,5 h" (PL) albo „—" dla zera. */
function fmtHours(h: number): string {
  if (!h) return '—';
  return `${(Math.round(h * 10) / 10).toLocaleString('pl-PL')} h`;
}

/** Kolumna weekendowa wg indeksu (0=Pon … 5=Sob, 6=Ndz). */
function isWeekendIdx(i: number): boolean {
  return i >= 5;
}

const WEEKEND_TINT = 'bg-gray-50/70 dark:bg-gray-800/40';

export function ScheduleGrid() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scope = useActiveCompanyScope();
  const companyId = scope.companyId ?? undefined;

  const [weekStart, setWeekStart] = useState(() => startOfWeekMonday(new Date()));
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );
  const dayKeys = useMemo(() => days.map(toLocalDateStr), [days]);

  const employeesQuery = useEmployeesList({ companyId, isActive: true, limit: 100 });
  const employees = useMemo(() => employeesQuery.data?.employees ?? [], [employeesQuery.data]);
  const empName = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of employees) m.set(e.id, `${e.firstName} ${e.lastName}`.trim());
    return m;
  }, [employees]);

  // Słownik `employee_positions` (mirror `OrgChart.tsx`/`Employees.tsx`) — backend zwraca
  // surowy kod stanowiska, etykieta rozwiązana z tego słownika.
  const positionsDict = useDictionary('employee_positions');
  const positionLabelByCode = useMemo(() => {
    const map = new Map<string, string>();
    if (!positionsDict.dictionary) return map;
    for (const item of positionsDict.items) {
      map.set(item.code, formatDictionaryOptionLabel(item, positionsDict.dictionary.displayMode));
    }
    return map;
  }, [positionsDict.dictionary, positionsDict.items]);

  const shiftsQuery = useQuery({
    queryKey: ['module-records', SHIFT_SLUG, 'schedule'],
    queryFn: async () => {
      const res = await api.get<{ records: ShiftRecord[] }>(
        `/modules/${SHIFT_SLUG}/records?limit=500`,
      );
      return res.data.records ?? [];
    },
    staleTime: 30_000,
  });
  const shifts = useMemo(() => shiftsQuery.data ?? [], [shiftsQuery.data]);

  // Bucket: `${employeeKey}|${dayKey}` → zmiany. employeeKey = pracownik id albo '__none__'.
  const NONE = '__none__';
  const byCell = useMemo(() => {
    const map = new Map<string, ShiftRecord[]>();
    for (const s of shifts) {
      if (!s.data_od) continue;
      const d = new Date(s.data_od);
      if (Number.isNaN(d.getTime())) continue;
      const dayKey = toLocalDateStr(d);
      if (!dayKeys.includes(dayKey)) continue; // spoza wyświetlanego tygodnia
      const empKey = s.pracownik && empName.has(s.pracownik) ? s.pracownik : NONE;
      const cellKey = `${empKey}|${dayKey}`;
      const arr = map.get(cellKey) ?? [];
      arr.push(s);
      map.set(cellKey, arr);
    }
    return map;
  }, [shifts, dayKeys, empName]);

  // Wiersze: pracownicy + (na dole) „Nieprzypisane" gdy są zmiany bez pracownika w tygodniu.
  const hasUnassigned = useMemo(
    () => dayKeys.some((dk) => (byCell.get(`${NONE}|${dk}`)?.length ?? 0) > 0),
    [byCell, dayKeys],
  );

  // Suma godzin w tygodniu per pracownik (empKey → godziny) + obsada per dzień.
  const weeklyHoursByEmp = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of shifts) {
      if (!s.data_od) continue;
      const d = new Date(s.data_od);
      if (Number.isNaN(d.getTime())) continue;
      const dayKey = toLocalDateStr(d);
      if (!dayKeys.includes(dayKey)) continue;
      const empKey = s.pracownik && empName.has(s.pracownik) ? s.pracownik : NONE;
      m.set(empKey, (m.get(empKey) ?? 0) + shiftHours(s));
    }
    return m;
  }, [shifts, dayKeys, empName]);

  const coverageByDay = useMemo(() => {
    const m = new Map<string, number>();
    for (const dk of dayKeys) m.set(dk, 0);
    for (const s of shifts) {
      if (!s.data_od) continue;
      const d = new Date(s.data_od);
      if (Number.isNaN(d.getTime())) continue;
      const dk = toLocalDateStr(d);
      if (m.has(dk)) m.set(dk, (m.get(dk) ?? 0) + 1);
    }
    return m;
  }, [shifts, dayKeys]);

  const weekLabel = `${toLocalDateStr(weekStart)} – ${toLocalDateStr(addDays(weekStart, 6))}`;

  function renderCell(empKey: string, dayKey: string, idx: number) {
    const cell = byCell.get(`${empKey}|${dayKey}`) ?? [];
    return (
      <td
        key={dayKey}
        className={`border-l border-gray-100 align-top dark:border-gray-800 ${
          isWeekendIdx(idx) ? WEEKEND_TINT : ''
        }`}
        style={{ minWidth: 120 }}
      >
        <div className="space-y-1 p-1">
          {cell.map((s) => {
            const typ = (s.typ ?? '') as string;
            return (
              <div
                key={s.id}
                className={`rounded border px-1.5 py-1 text-[11px] leading-tight ${TYP_CLASSES[typ] ?? TYP_DEFAULT}`}
                title={s.record_name ?? undefined}
              >
                <div className="font-semibold">
                  {fmtTime(s.data_od)}
                  {s.data_do ? `–${fmtTime(s.data_do)}` : ''}
                </div>
                <div className="opacity-90">{TYP_LABEL[typ] ? t(TYP_LABEL[typ]) : typ}</div>
                {s.miejsce && <div className="truncate opacity-75">{String(s.miejsce)}</div>}
              </div>
            );
          })}
        </div>
      </td>
    );
  }

  const loading = employeesQuery.isLoading || shiftsQuery.isLoading;
  // 404 z `/modules/grafiki_zmiany/records` = moduł Kreatora nie jest zainstalowany u tenanta
  // (np. subskrypcja bez instalacji rodzaju danych) — wtedy formularz „Nowa zmiana" też nie istnieje.
  const shiftsMissing =
    shiftsQuery.isError &&
    isAxiosError(shiftsQuery.error) &&
    shiftsQuery.error.response?.status === 404;

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-6 py-4 dark:border-gray-700">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('tenant.schedule.title')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('tenant.schedule.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <IconButton
            variant="subtle"
            size="sm"
            onClick={() => setWeekStart((w) => addDays(w, -7))}
            aria-label={t('tenant.schedule.prevWeek')}
          >
            <ChevronLeft aria-hidden="true" />
          </IconButton>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setWeekStart(startOfWeekMonday(new Date()))}
          >
            {t('tenant.schedule.today')}
          </Button>
          <IconButton
            variant="subtle"
            size="sm"
            onClick={() => setWeekStart((w) => addDays(w, 7))}
            aria-label={t('tenant.schedule.nextWeek')}
          >
            <ChevronRight aria-hidden="true" />
          </IconButton>
          <span className="ml-1 text-sm font-medium text-gray-600 dark:text-gray-300">
            {weekLabel}
          </span>
          {!shiftsQuery.isError && (
            <Button
              variant="primary"
              size="sm"
              className="ml-2"
              onClick={() => navigate(`/modules/${SHIFT_SLUG}/records/new`)}
              leftIcon={<CalendarPlus size={16} aria-hidden="true" />}
              data-testid="schedule-new-shift-btn"
            >
              {t('tenant.schedule.newShift')}
            </Button>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {loading ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-gray-400">
            {t('tenant.schedule.loading')}
          </div>
        ) : shiftsQuery.isError ? (
          <div data-testid={shiftsMissing ? 'schedule-unavailable' : 'schedule-error'}>
            <EmptyState
              icon={CalendarOff}
              title={t(
                shiftsMissing ? 'tenant.schedule.unavailableTitle' : 'tenant.schedule.errorTitle',
              )}
              description={t(
                shiftsMissing
                  ? 'tenant.schedule.unavailableDescription'
                  : 'tenant.schedule.errorDescription',
              )}
              action={
                shiftsMissing ? undefined : (
                  <Button variant="secondary" size="sm" onClick={() => shiftsQuery.refetch()}>
                    {t('tenant.schedule.retry')}
                  </Button>
                )
              }
            />
          </div>
        ) : (
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="w-48 border-b border-gray-200 px-3 py-2 text-left font-medium text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  {t('tenant.schedule.employee')}
                </th>
                {days.map((d, i) => {
                  const dayKey = WEEK_DAYS_PL[i];
                  return (
                    <th
                      key={dayKeys[i]}
                      className={`border-b border-l border-gray-200 px-2 py-2 text-left font-medium text-gray-500 dark:border-gray-700 dark:text-gray-400 ${
                        isWeekendIdx(i) ? WEEKEND_TINT : ''
                      }`}
                    >
                      <div>{dayKey ? t(dayKey) : ''}</div>
                      <div className="text-xs font-normal text-gray-400">{fmtDayHeader(d)}</div>
                    </th>
                  );
                })}
                <th className="border-b border-l border-gray-200 px-2 py-2 text-right font-medium text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  {t('tenant.schedule.total')}
                </th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => (
                <tr key={e.id} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {empName.get(e.id)}
                    </div>
                    {e.positionCode && (
                      <div className="text-xs text-gray-400">
                        {positionLabelByCode.get(e.positionCode) ?? e.positionCode}
                      </div>
                    )}
                  </td>
                  {dayKeys.map((dk, i) => renderCell(e.id, dk, i))}
                  <td className="border-l border-gray-100 px-2 py-2 text-right align-top font-semibold text-gray-700 dark:border-gray-800 dark:text-gray-200">
                    {fmtHours(weeklyHoursByEmp.get(e.id) ?? 0)}
                  </td>
                </tr>
              ))}
              {hasUnassigned && (
                <tr className="border-b border-gray-100 bg-gray-50/50 dark:border-gray-800 dark:bg-gray-800/30">
                  <td className="px-3 py-2 align-top italic text-gray-500 dark:text-gray-400">
                    {t('tenant.schedule.unassigned')}
                  </td>
                  {dayKeys.map((dk, i) => renderCell(NONE, dk, i))}
                  <td className="border-l border-gray-100 px-2 py-2 text-right align-top font-semibold text-gray-700 dark:border-gray-800 dark:text-gray-200">
                    {fmtHours(weeklyHoursByEmp.get(NONE) ?? 0)}
                  </td>
                </tr>
              )}
              {employees.length === 0 && !hasUnassigned && (
                <tr>
                  <td
                    colSpan={9}
                    className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    {t('tenant.schedule.empty')}
                  </td>
                </tr>
              )}
            </tbody>
            {(employees.length > 0 || hasUnassigned) && (
              <tfoot>
                <tr className="border-t border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60">
                  <td className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('tenant.schedule.coverage')}
                  </td>
                  {dayKeys.map((dk, i) => (
                    <td
                      key={dk}
                      className={`border-l border-gray-100 px-2 py-2 text-center text-sm text-gray-600 dark:border-gray-800 dark:text-gray-300 ${
                        isWeekendIdx(i) ? WEEKEND_TINT : ''
                      }`}
                    >
                      {coverageByDay.get(dk) ?? 0}
                    </td>
                  ))}
                  <td className="border-l border-gray-100 dark:border-gray-800" />
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
    </div>
  );
}
